import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { http } from "@/lib/api";

const AuthCtx = createContext(null);

// Auth is now httpOnly cookie-based (Sprint 30).
//
// The backend sets the `wp_auth` cookie on /auth/signup, /auth/login, and
// /auth/admin-reset. Every axios call attaches it automatically because
// `http` was created with `withCredentials: true` in /app/frontend/src/lib/api.js.
//
// Legacy JWT-in-localStorage cleanup: on mount we scrub any stale
// `warpaint_jwt` value so old sessions don't leak into new devtools inspections
// after the migration. Chrome extension continues to use its own
// chrome.storage.local + Authorization: Bearer path, unaffected by this.
const LEGACY_LS_TOKEN = "warpaint_jwt";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await http.get("/auth/me");
      setUser(r.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // One-time scrub of the pre-Sprint-30 localStorage token
    try { localStorage.removeItem(LEGACY_LS_TOKEN); } catch { /* noop */ }
    // If Google OAuth callback is in progress, let AuthCallback handle it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signup = async (email, password, name, inviteCode, extras = {}) => {
    const payload = { email, password, name, ...extras };
    if (inviteCode) payload.invite_code = inviteCode;
    const r = await http.post("/auth/signup", payload);
    // Cookie is now set by the server; hydrate the user from /auth/me.
    await checkAuth();
    return r.data;
  };

  const login = async (email, password) => {
    const r = await http.post("/auth/login", { email, password });
    await checkAuth();
    return r.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await http.post("/auth/logout", {}); } catch (e) { /* noop */ }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, checkAuth, signup, login, loginWithGoogle, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
